import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { apiClient } from "../utils/http";
import { DELETE_CONFIRM, PERMISSIONS } from "../utils/constants";
import { todayISO } from "../utils/format";
import AppHeader from "../components/AppHeader";
import MetricCards from "../components/MetricCards";
import AnalyticsDashboard from "../components/AnalyticsDashboard";
import ControlsBar from "../components/ControlsBar";
import JobTable from "../components/JobTable";
import JobOrderWizard from "../components/wizard/JobOrderWizard";
import JobOrderPreview from "../components/preview/JobOrderPreview";
import useSchedule from "../components/schedule/useSchedule";
import ScheduleWidget from "../components/schedule/ScheduleWidget";
import CalendarModal from "../components/schedule/CalendarModal";
import DateDetailsModal from "../components/schedule/DateDetailsModal";
import EventModal from "../components/schedule/EventModal";
import UsersModal from "../components/users/UsersModal";

const ROWS_PER_PAGE = 25;
const EXPORT_TIMEOUT_MS = 60000;

export default function Dashboard() {
  const { user, config, can, logout } = useAuth();
  const showToast = useToast();
  const navigate = useNavigate();
  const currency = config.default_currency;
  const perms = {
    add: can(PERMISSIONS.ADD),
    update: can(PERMISSIONS.UPDATE),
    delete: can(PERMISSIONS.DELETE),
  };

  const [dashboard, setDashboard] = useState(null);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [chartMetric, setChartMetric] = useState("count");
  const [filters, setFilters] = useState({ search: "", status: "ALL", service: "ALL" });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [list, setList] = useState({ rows: [], total: 0, filteredTotal: 0, page: 1, lastPage: 1 });
  const [listLoading, setListLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [wizard, setWizard] = useState(null);
  const [preview, setPreview] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [usersOpen, setUsersOpen] = useState(false);
  const canManageUsers = Boolean(user.can_manage) || user.user_type === "store_owner" || Boolean(user.is_store_admin);
  const openSeq = useRef(0);
  const schedule = useSchedule(showToast);

  // Job orders feed the schedule too (their deadlines), so it reloads with them.
  const refresh = () => {
    setRefreshKey((key) => key + 1);
    schedule.refresh();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(filters.search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const params = useMemo(
    () => ({ search, status: filters.status, service: filters.service }),
    [search, filters.status, filters.service]
  );

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get("dashboard", { params: { year } })
      .then((res) => !cancelled && setDashboard(res.data))
      .catch((err) => !cancelled && showToast(err.message, "error"));
    return () => {
      cancelled = true;
    };
  }, [year, refreshKey, showToast]);

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    apiClient
      .get("job-orders", { params: { ...params, page, rowsPerPage: ROWS_PER_PAGE } })
      .then((res) => {
        if (cancelled) return;
        const paginated = res.data.job_orders;
        // e.g. the last row of the last page was deleted
        if (paginated.data.length === 0 && paginated.current_page > 1) {
          setPage(paginated.last_page || 1);
          return;
        }
        setList({
          rows: paginated.data,
          total: res.data.total,
          filteredTotal: paginated.total,
          page: paginated.current_page,
          lastPage: paginated.last_page,
        });
        setListLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setListLoading(false);
        showToast(err.message, "error");
      });
    return () => {
      cancelled = true;
    };
  }, [params, page, refreshKey, showToast]);

  const changeFilters = (patch) => {
    setFilters((current) => ({ ...current, ...patch }));
    if ("status" in patch || "service" in patch) setPage(1);
  };

  const fetchJob = useCallback(
    async (job) => {
      try {
        const res = await apiClient.get(`job-orders/${job.id}`);
        return res.data.job_order;
      } catch (err) {
        showToast(err.message, "error");
        return null;
      }
    },
    [showToast]
  );

  const openPreview = (job) => {
    openSeq.current += 1;
    setPreview({ job, key: openSeq.current });
  };

  const handleView = async (job) => {
    const full = await fetchJob(job);
    if (full) openPreview(full);
  };

  const handleEdit = async (job) => {
    const full = await fetchJob(job);
    if (full) {
      setPreview(null);
      setWizard({ job: full });
    }
  };

  const handleNew = () => {
    setPreview(null);
    setWizard({ job: null });
  };

  const handleDelete = async (job) => {
    if (!window.confirm(DELETE_CONFIRM)) return;
    try {
      const res = await apiClient.delete(`job-orders/delete/${job.id}`);
      showToast(res.data.message || "Job Order deleted", "info");
      refresh();
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const handleSaved = (saved) => {
    setWizard(null);
    refresh();
    openPreview(saved);
  };

  /* "View Order" / "Edit Job Order" on a deadline in the calendar's day details; the calendar stays open underneath. */
  const handleScheduleJob = (open) => (id) => {
    schedule.closeDay();
    open({ id });
  };

  const handleStatusClick = (status) => {
    changeFilters({ status });
    const table = document.querySelector(".table-container-card");
    if (table) table.scrollIntoView({ behavior: "smooth" });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get("job-orders/export", {
        params,
        responseType: "blob",
        timeout: EXPORT_TIMEOUT_MS,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Job_Register_${todayISO()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showToast("Job register exported");
    } catch (err) {
      showToast(err.status ? "Export failed, please try again" : err.message, "error");
    } finally {
      setExporting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    showToast("Signed out of Job Register Portal", "info");
    navigate("/login", { replace: true });
  };

  return (
    <div className="app-container">
      <AppHeader
        user={user}
        canAdd={perms.add}
        exporting={exporting}
        onOpenCalendar={schedule.openCalendar}
        onOpenUsers={canManageUsers ? () => setUsersOpen(true) : null}
        onExport={handleExport}
        onNew={handleNew}
        onLogout={handleLogout}
      />

      <main className="main-content no-print">
        {dashboard ? (
          <>
            <MetricCards dashboard={dashboard} currency={currency} />
            <AnalyticsDashboard
              dashboard={dashboard}
              currency={currency}
              year={year}
              onYearChange={setYear}
              metric={chartMetric}
              onMetricChange={setChartMetric}
              onStatusClick={handleStatusClick}
            >
              <ScheduleWidget
                version={schedule.version}
                canAdd={perms.add}
                onOpenCalendar={schedule.openCalendar}
                onOpenDay={schedule.openCalendar}
                onAdd={schedule.openEventForm}
              />
            </AnalyticsDashboard>
          </>
        ) : (
          <div className="app-loader" style={{ minHeight: 240 }}>
            <div className="spinner" />
          </div>
        )}

        <ControlsBar filters={filters} services={config.services} onChange={changeFilters} />

        <JobTable
          list={list}
          loading={listLoading}
          perms={perms}
          onPage={setPage}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onNew={handleNew}
        />
      </main>

      {schedule.calendarOpen && <CalendarModal schedule={schedule} canAdd={perms.add} />}

      {schedule.dayDate && (
        <DateDetailsModal
          key={schedule.dayDate}
          date={schedule.dayDate}
          schedule={schedule}
          perms={perms}
          onViewJob={handleScheduleJob(handleView)}
          onEditJob={handleScheduleJob(handleEdit)}
        />
      )}

      {schedule.eventForm && (
        <EventModal
          eventForm={schedule.eventForm}
          onClose={schedule.closeEventForm}
          onSaved={() => {
            schedule.closeEventForm();
            schedule.refresh();
          }}
        />
      )}

      {usersOpen && <UsersModal onClose={() => setUsersOpen(false)} onSettingsChanged={schedule.refresh} />}

      {wizard && (
        <JobOrderWizard
          key={wizard.job ? `edit-${wizard.job.id}` : "new"}
          job={wizard.job}
          onClose={() => setWizard(null)}
          onSaved={handleSaved}
        />
      )}

      {preview && (
        <JobOrderPreview key={preview.key} job={preview.job} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}
